// ============================================================================
// CANCUN100 — BalanceReal.js  (Bloque 53 · «Balance real», arriba del Resumen global, solo super admin)
// ----------------------------------------------------------------------------
// Copia la referencia aprobada por Marco (BLOQUE_53_referencia_balance_real.html): estilo propio solo en esta sección
// (fondo claro, tarjeta de banco azul, Manrope + JetBrains Mono). Todo es LECTURA: resumen_periodo, balances_por_cobrar,
// proveedores_devengado, pagos_proveedor, prepagos y libro_caja, desde DIA_CERO. Sin escrituras. Cada renglón lleva el
// nombre sencillo y debajo el contable (regla de Marco: dos idiomas). «por calcular» / «por definir» quedan como texto
// hasta el plan maestro 2.1.
// ============================================================================
import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from './supabaseClient';
import { DIA_CERO, SALDO_INICIAL, nombreCuenta } from './contabilidadV3';

const K = {
  fondo: '#F4F7FC', panel: '#FFFFFF', borde: '#DCE4F0', sep: '#EDF1F7', texto: '#0F1B33', sec: '#55657F', tenue: '#6E7C96', fila: '#2A3B5C',
  azul: '#1D5FD1', rojo: '#C0304E', ambar: '#A86A12', tarjeta: '#12306E', tarjetaBorde: '#2D5BBF', tarjetaTexto: '#BFD3FF', aro: '#3E6FD8', chip: '#C9D6EE',
};
const MONO = "'JetBrains Mono', ui-monospace, monospace";
const SANS = "'Manrope', system-ui, sans-serif";
const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const MES_CORTO = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const nombreMes = (p) => { const [y, m] = String(p || '').split('-'); return MESES[Number(m) - 1] ? `${MESES[Number(m) - 1]} ${y}` : p; };
const soloMes = (p) => MESES[Number(String(p || '').split('-')[1]) - 1] || '';
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const num = (v) => { const x = Number(v); return Number.isFinite(x) ? x : 0; };
const money = (v) => `${v < 0 ? '−' : ''}$${Math.round(Math.abs(num(v))).toLocaleString('en-US')}`;
const ymHoy = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; };

const PROVEEDORES = ['xperiencias_caribe', 'live_caribe', 'transportes'];
const HOTELES = ['hoteles'];
const VUELOS = ['vuelos'];

// Fuentes de Google cargadas solo cuando esta sección se monta (una vez).
const useFuentesB53 = () => {
  useEffect(() => {
    if (typeof document === 'undefined' || document.getElementById('b53-fuentes')) return;
    const l = document.createElement('link');
    l.id = 'b53-fuentes'; l.rel = 'stylesheet';
    l.href = 'https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600&display=swap';
    document.head.appendChild(l);
  }, []);
};

// Bloque 54: selector propio para la pestaña «💳 Balance real» (en el Resumen de Marco usa el selector del Resumen).
const ymDe = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
const OPCIONES_PERIODO = [['mes', 'Este mes'], ['mes-pasado', 'Mes pasado'], ['q4', 'Q4'], ['anio', 'Año'], ['todos', 'Todos']];
const mesesDeModo = (modo) => {
  const hoy = new Date(); const y = hoy.getFullYear();
  if (modo === 'mes') return [ymDe(hoy)];
  if (modo === 'mes-pasado') return [ymDe(new Date(y, hoy.getMonth() - 1, 1))];
  if (modo === 'q4') return [`${y}-10`, `${y}-11`, `${y}-12`];
  if (modo === 'anio') return Array.from({ length: 12 }, (_, i) => `${y}-${String(i + 1).padStart(2, '0')}`);
  return null; // todos
};
const etiquetaDeModo = (modo) => { const y = new Date().getFullYear(); return modo === 'q4' ? `Q4 ${y} (oct-dic)` : modo === 'anio' ? `Año ${y}` : modo === 'todos' ? 'Todos (desde el día cero)' : ''; };

export default function BalanceReal({ meses: mesesProp = null, etiqueta: etiquetaProp = '', isMobile = false, conSelector = false }) {
  useFuentesB53();
  const [modo, setModo] = useState('mes'); // B54: solo cuenta con conSelector
  const meses = conSelector ? mesesDeModo(modo) : mesesProp;
  const etiqueta = conSelector ? etiquetaDeModo(modo) : etiquetaProp;
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const [abierto, setAbierto] = useState({ prov: true, hot: false, vue: false });

  useEffect(() => {
    let vivo = true;
    (async () => {
      const [rp, bpc, dev, pp, pre, gas] = await Promise.all([
        supabase.from('resumen_periodo').select('periodo,dep_iniciales,balances,balances_llegada,balances_transporte,ajustes_cobrados,entradas,salidas,neto,n_reservas_nuevas,n_pagos_posteriores,comisiones_pagadas').gte('periodo', DIA_CERO),
        supabase.from('balances_por_cobrar').select('reserva_id,pendiente,periodo_venta').gte('periodo_venta', DIA_CERO),
        supabase.from('proveedores_devengado').select('cuenta_proveedor,reserva_id,neto,periodo').gte('periodo', DIA_CERO),
        supabase.from('pagos_proveedor').select('cuenta_proveedor,monto,periodo').gte('periodo', DIA_CERO),
        supabase.from('prepagos').select('cuenta_proveedor,monto,periodo,pagado').eq('pagado', true).gte('periodo', DIA_CERO),
        supabase.from('libro_caja').select('subtipo,monto,periodo').eq('origen', 'gasto').gte('periodo', DIA_CERO),
      ]);
      if (!vivo) return;
      const err = [rp, bpc, dev, pp, pre, gas].find((r) => r.error);
      if (err) setError(err.error.message);
      setD({ rp: rp.data || [], bpc: bpc.data || [], dev: dev.data || [], pp: pp.data || [], pre: pre.data || [], gas: gas.data || [] });
    })();
    return () => { vivo = false; };
  }, []);

  const c = useMemo(() => {
    if (!d) return null;
    const enPer = (p) => p >= DIA_CERO && (!meses || meses.includes(p));
    const ultimo = meses && meses.length ? meses[meses.length - 1] : '9999-12';
    const mesesValidos = meses ? meses.filter((m) => m >= DIA_CERO) : null;
    const primero = mesesValidos && mesesValidos.length ? mesesValidos[0] : DIA_CERO;
    const antes = ultimo < DIA_CERO;
    // Caja: saldo inicial + Σ neto desde el día cero hasta el fin del periodo
    const netoHasta = d.rp.filter((r) => r.periodo <= ultimo).reduce((s, r) => s + num(r.neto), 0);
    const netoAntesDelPeriodo = d.rp.filter((r) => r.periodo < primero).reduce((s, r) => s + num(r.neto), 0);
    const banco = (SALDO_INICIAL ?? 0) + netoHasta;
    const arranque = (SALDO_INICIAL ?? 0) + netoAntesDelPeriodo;
    // Flujo del periodo
    const t = { dep: 0, bal: 0, ajc: 0, ent: 0, sal: 0, nRes: 0, nPag: 0, com: 0 };
    d.rp.filter((r) => enPer(r.periodo)).forEach((r) => {
      t.dep += num(r.dep_iniciales); t.bal += num(r.balances) + num(r.balances_llegada) + num(r.balances_transporte);
      t.ajc += num(r.ajustes_cobrados); t.ent += num(r.entradas); t.sal += num(r.salidas);
      t.nRes += num(r.n_reservas_nuevas); t.nPag += num(r.n_pagos_posteriores); t.com += num(r.comisiones_pagadas);
    });
    const sinClasificar = t.ent - (t.dep + t.bal + t.ajc);
    // Por cobrar
    const porCobrar = d.bpc.reduce((s, b) => s + num(b.pendiente), 0);
    const nFolios = new Set(d.bpc.map((b) => b.reserva_id)).size;
    // Por pagar por cuenta (nunca < 0)
    const cuentas = {};
    const cta = (k) => (cuentas[k || 'sin_cuenta'] = cuentas[k || 'sin_cuenta'] || { dev: 0, pag: 0, pre: 0, folios: new Set() });
    d.dev.forEach((r) => { const x = cta(r.cuenta_proveedor); x.dev += num(r.neto); if (r.reserva_id != null) x.folios.add(r.reserva_id); });
    d.pp.forEach((r) => { cta(r.cuenta_proveedor).pag += num(r.monto); });
    d.pre.forEach((r) => { cta(r.cuenta_proveedor).pre += num(r.monto); });
    const saldo = (k) => { const x = cuentas[k]; return x ? Math.max(0, x.dev - x.pag - x.pre) : 0; };
    const folios = (k) => (cuentas[k] ? cuentas[k].folios.size : 0);
    const otras = Object.keys(cuentas).filter((k) => ![...PROVEEDORES, ...HOTELES, ...VUELOS].includes(k));
    const grupo = (ks) => ks.map((k) => ({ k, nombre: k === 'sin_cuenta' ? 'Sin cuenta' : nombreCuenta(k), saldo: saldo(k), folios: folios(k) }));
    const prov = grupo([...PROVEEDORES, ...otras]); const hot = grupo(HOTELES); const vue = grupo(VUELOS);
    const sum = (g) => g.reduce((s, x) => s + x.saldo, 0);
    const porPagar = sum(prov) + sum(hot) + sum(vue);
    // Gastos del periodo
    const gastos = { fijo: 0, adicional: 0 };
    d.gas.filter((g) => enPer(g.periodo)).forEach((g) => { if (g.subtipo === 'fijo') gastos.fijo += Math.abs(num(g.monto)); else gastos.adicional += Math.abs(num(g.monto)); });
    return {
      antes, banco, arranque, netoAntesDelPeriodo, primero, ultimo, t, sinClasificar, porCobrar, nFolios, prov, hot, vue, sum, porPagar, gastos,
      final: banco + porCobrar - porPagar,
      corteTxt: (() => { const hoy = new Date(); if (!meses || meses.includes(ymHoy())) return `al ${hoy.getDate()} ${MES_CORTO[hoy.getMonth()]}`; const [y, m] = ultimo.split('-').map(Number); const fin = new Date(y, m, 0); return `al ${fin.getDate()} ${MES_CORTO[fin.getMonth()]}`; })(),
    };
  }, [d, meses && meses.join(',')]); // eslint-disable-line

  // ── Estilos ──
  const panel = { background: K.panel, border: `1px solid ${K.borde}`, borderRadius: 16 };
  const cifra = (extra) => ({ fontFamily: MONO, fontVariantNumeric: 'tabular-nums', ...extra });
  const rotulo = { fontSize: 12, letterSpacing: '0.14em', color: K.sec, fontWeight: 700 };
  const dosNombres = (sencillo, contable, extra = null) => (
    <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
      <span>{sencillo}{extra}</span>
      <span style={{ fontSize: 12, color: K.tenue, fontWeight: 500 }}>{contable}</span>
    </span>
  );
  const filaFlujo = (sencillo, contable, valor, colorValor = K.texto, extra = null, ultima = false) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, fontSize: 14, padding: '9px 0', borderBottom: ultima ? 'none' : `1px solid ${K.sep}` }}>
      {dosNombres(sencillo, contable, extra)}
      <span style={cifra({ color: colorValor, whiteSpace: 'nowrap' })}>{valor}</span>
    </div>
  );
  const colsAcordeon = isMobile ? 'minmax(0, 1fr) auto 18px' : 'minmax(0, 1fr) 130px 130px 32px'; // móvil: sin la columna «Meses siguientes» (va como línea chica)

  const raiz = { background: K.fondo, color: K.texto, fontFamily: SANS, borderRadius: 20, padding: isMobile ? '22px 16px 24px' : '32px 36px 36px', display: 'flex', flexDirection: 'column', gap: isMobile ? 18 : 24, marginBottom: 20, boxSizing: 'border-box' };

  if (!c) return <div style={raiz} data-seccion="balance-real"><span style={{ color: K.sec, fontSize: 14 }}>{error ? `No se pudo leer el balance real: ${error}` : 'Cargando balance real…'}</span></div>;

  const mesArranque = soloMes(c.primero);
  const mesCorte = c.ultimo === '9999-12' ? soloMes(ymHoy()) : soloMes(c.ultimo);
  const sinSaldo = SALDO_INICIAL == null;

  // ── Encabezado ──
  const selector = conSelector ? (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }} role="group" aria-label="Periodo">
      {OPCIONES_PERIODO.map(([k, l]) => (
        <button key={k} type="button" aria-pressed={modo === k} onClick={() => setModo(k)}
          style={{ all: 'unset', cursor: 'pointer', padding: '8px 14px', borderRadius: 10, fontSize: 14, fontWeight: modo === k ? 700 : 600, fontFamily: SANS, color: modo === k ? '#ffffff' : K.sec, background: modo === k ? K.tarjeta : K.panel, border: `1px solid ${modo === k ? K.tarjetaBorde : K.borde}` }}>{l}</button>
      ))}
    </div>
  ) : null;
  const encabezado = (
    <div style={{ display: 'flex', alignItems: isMobile ? 'flex-start' : 'flex-end', justifyContent: 'space-between', flexDirection: isMobile ? 'column' : 'row', gap: 12 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span style={{ fontSize: 12, letterSpacing: '0.18em', color: K.sec, fontWeight: 600 }}>CONTABILIDAD · BALANCE REAL</span>
        <h2 style={{ margin: 0, fontSize: isMobile ? 24 : 30, fontWeight: 800, letterSpacing: '-0.01em', color: K.texto, fontFamily: SANS }}>Lo que debe haber en Cancun100</h2>
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 14, color: K.sec }}>Corte</span>
        <span style={{ padding: '8px 14px', border: '1px solid #C9D5E8', borderRadius: 10, fontSize: 14, fontWeight: 600, background: K.panel }}>{meses && meses.length === 1 ? cap(nombreMes(meses[0])) : (etiqueta || cap(nombreMes(c.ultimo)))}</span>
        <span style={{ padding: '8px 14px', border: `1px solid ${K.borde}`, borderRadius: 10, fontSize: 14, color: K.sec }}>Día cero · 1 {MES_CORTO[Number(DIA_CERO.split('-')[1]) - 1]}</span>
      </div>
    </div>
  );

  if (c.antes) {
    return (
      <div style={raiz} data-seccion="balance-real">
        {selector}
        {encabezado}
        <div style={{ ...panel, padding: '22px 26px', fontSize: 14, color: K.sec }}>
          <strong style={{ color: K.texto }}>Antes del día cero.</strong> El balance real empieza el 1 de {soloMes(DIA_CERO)} {DIA_CERO.slice(0, 4)}; este periodo no se suma.
        </div>
      </div>
    );
  }

  // ── Tarjeta de banco ──
  const tarjeta = (
    <div style={{ width: isMobile ? '100%' : 470, ...(isMobile ? { minHeight: 230 } : { height: 290, alignSelf: 'flex-start' }), flexShrink: 0, boxSizing: 'border-box', borderRadius: 20, padding: isMobile ? '22px 22px' : '28px 30px', background: K.tarjeta, border: `1px solid ${K.tarjetaBorde}`, color: '#ffffff', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: 16, position: 'relative', overflow: 'hidden', boxShadow: '0 20px 44px rgba(18,48,110,0.28)' }}>
      <svg viewBox="0 0 470 290" preserveAspectRatio="xMaxYMin slice" aria-hidden="true" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
        <circle cx="420" cy="20" r="160" fill="none" stroke={K.aro} strokeOpacity="0.35" strokeWidth="1" />
        <circle cx="420" cy="20" r="210" fill="none" stroke={K.aro} strokeOpacity="0.22" strokeWidth="1" />
        <circle cx="420" cy="20" r="260" fill="none" stroke={K.aro} strokeOpacity="0.14" strokeWidth="1" />
      </svg>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative' }}>
        <span style={{ fontSize: 22, fontWeight: 800, letterSpacing: '0.04em' }}>CANCUN100</span>
        <span style={{ fontSize: 12, letterSpacing: '0.2em', color: K.tarjetaTexto, fontWeight: 600 }}>BALANCE REAL</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, position: 'relative' }}>
        <div style={{ width: 46, height: 34, borderRadius: 7, background: K.chip, border: '1px solid #E6EDFA' }} />
        <span style={cifra({ fontSize: 14, color: K.tarjetaTexto, letterSpacing: '0.16em' })}>•••• •••• 0100</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, position: 'relative' }}>
        <span style={{ fontSize: 12, letterSpacing: '0.16em', color: K.tarjetaTexto, fontWeight: 600 }}>DEBE HABER EN BANCO · SALDO DE CAJA</span>
        <span style={cifra({ fontSize: isMobile ? 36 : 46, fontWeight: 600, letterSpacing: '-0.02em', lineHeight: 1.1 })}>{money(c.banco)}</span>
        <span style={{ fontSize: 12, color: K.tarjetaTexto }}>BBVA + Mercado Pago · MXN · {c.corteTxt}</span>
        {sinSaldo && <span style={{ fontSize: 12, color: K.tarjetaTexto, opacity: 0.85 }}>sin saldo inicial · por definir</span>}
      </div>
    </div>
  );

  // ── De dónde sale ──
  const valArranque = sinSaldo ? (c.netoAntesDelPeriodo ? `${money(c.netoAntesDelPeriodo)} + saldo inicial` : 'por definir') : money(c.arranque);
  const deDonde = (
    <div style={{ ...panel, flexGrow: 1, minWidth: 0, padding: isMobile ? '18px 16px' : '22px 26px', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <h3 style={{ margin: '0 0 10px 0', fontSize: 14, letterSpacing: '0.12em', color: K.sec, fontWeight: 700, fontFamily: SANS }}>DE DÓNDE SALE ESA CANTIDAD</h3>
      {filaFlujo(`En bolsa al arrancar ${mesArranque}`, (SALDO_INICIAL === 0 && c.primero === DIA_CERO)
        ? <>Saldo inicial · anticipos de clientes<br />Arranque en ceros · se concilia con el banco al cierre de {soloMes(DIA_CERO)}</>
        : 'Saldo inicial · anticipos de clientes', valArranque, K.azul)} {/* B54: arranque en ceros */}
      {filaFlujo('+ Depósitos iniciales', `Ingresos por anticipos · ${c.t.nRes} reserva${c.t.nRes === 1 ? '' : 's'}`, money(c.t.dep))}
      {filaFlujo('+ Balances', `Cobranza${c.t.nPag ? ` · ${c.t.nPag} pago${c.t.nPag === 1 ? '' : 's'}` : ''}`, money(c.t.bal))}
      {filaFlujo('+ Pagos sin clasificar', 'Ingresos por identificar', money(c.sinClasificar), K.texto, c.sinClasificar > 0.5 ? <span style={{ color: K.ambar }}> · revisar</span> : null)}
      {filaFlujo('− Lo que salió', 'Egresos · proveedores, gastos y comisiones', c.t.sal ? `−${money(c.t.sal)}` : money(0), c.t.sal ? K.rojo : K.tenue)}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, fontSize: 16, fontWeight: 700, padding: '12px 0 4px 0' }}>
        {dosNombres('= Debe haber en banco', 'Saldo final de caja')}
        <span style={cifra({ whiteSpace: 'nowrap' })}>{money(c.banco)}{sinSaldo ? ' + bolsa' : ''}</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12, paddingTop: 10 }}>
        {[['Apartado para pagar', 'Pasivo · cuentas por pagar', K.rojo], ['Libre', 'Utilidad disponible', K.azul]].map(([a, b, col]) => (
          <div key={a} style={{ background: K.fondo, border: `1px solid ${K.borde}`, borderRadius: 12, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontSize: 14, color: K.fila }}>{a}</span>
            <span style={{ fontSize: 12, color: K.tenue }}>{b}</span>
            <span style={cifra({ fontSize: 22, color: col })}>por calcular</span>
          </div>
        ))}
      </div>
    </div>
  );

  // ── Tres cifras ──
  const tres = (
    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, minmax(0, 1fr))', gap: isMobile ? 12 : 18 }}>
      {[
        ['EN BANCO', money(c.banco), K.azul, 'Dinero real que ya entró, menos lo que ya salió'],
        ['POR COBRAR A CLIENTES', money(c.porCobrar), K.fila, `Balances futuros · ${c.nFolios} folio${c.nFolios === 1 ? '' : 's'}`],
        ['POR PAGAR', money(c.porPagar), K.rojo, 'Proveedores, hoteles y aerolíneas'],
      ].map(([r, v, col, t]) => (
        <div key={r} style={{ ...panel, padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <span style={rotulo}>{r}</span>
          <span style={cifra({ fontSize: 30, fontWeight: 600, color: col })}>{v}</span>
          <span style={{ fontSize: 14, color: K.sec }}>{t}</span>
        </div>
      ))}
    </div>
  );

  // ── Acordeones ──
  const acordeon = (clave, titulo, items, vacio, ultimo = false) => {
    const total = c.sum(items); const abiertoG = abierto[clave];
    return (
      <React.Fragment key={clave}>
        <button type="button" aria-expanded={abiertoG} onClick={() => setAbierto((a) => ({ ...a, [clave]: !a[clave] }))}
          style={{ all: 'unset', cursor: 'pointer', display: 'grid', gridTemplateColumns: colsAcordeon, gap: 12, padding: '16px 0', borderBottom: abiertoG || !ultimo ? `1px solid ${K.sep}` : 'none', fontSize: 16, fontWeight: 700, color: K.texto, fontFamily: SANS }}>
          <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>{titulo}{isMobile && <span style={{ fontSize: 12, fontWeight: 500, color: K.tenue }}>Meses siguientes · por calcular</span>}</span>
          <span style={cifra({ textAlign: 'right', color: total ? K.texto : K.tenue })}>{money(total)}</span>
          {!isMobile && <span style={cifra({ textAlign: 'right', color: K.tenue })}>por calcular</span>}
          <span style={{ textAlign: 'right', color: K.sec }}>{abiertoG ? '−' : '+'}</span>
        </button>
        {abiertoG && (total === 0 && vacio
          ? <div style={{ padding: '10px 0 14px 16px', fontSize: 14, color: K.sec, borderBottom: ultimo ? 'none' : `1px solid ${K.sep}` }}>{vacio}</div>
          : (
            <div style={{ display: 'flex', flexDirection: 'column', padding: '4px 0 10px 16px', borderBottom: ultimo ? 'none' : `1px solid ${K.sep}` }}>
              {items.map((x) => (
                <div key={x.k} style={{ display: 'grid', gridTemplateColumns: colsAcordeon, gap: 12, fontSize: 14, padding: '8px 0' }}>
                  <span style={{ color: K.fila }}>{x.nombre}{x.folios ? <span style={{ color: K.tenue }}> · {x.folios} folio{x.folios === 1 ? '' : 's'}</span> : null}</span>
                  <span style={cifra({ textAlign: 'right', color: x.saldo ? K.texto : K.tenue })}>{money(x.saldo)}</span>
                  {!isMobile && <span style={cifra({ textAlign: 'right', color: K.tenue })}>—</span>}
                  <span />
                </div>
              ))}
            </div>
          ))}
      </React.Fragment>
    );
  };
  const deudas = (
    <div style={{ ...panel, padding: isMobile ? '6px 14px' : '10px 22px', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <div style={{ display: 'grid', gridTemplateColumns: colsAcordeon, gap: 12, fontSize: 12, color: K.sec, padding: '12px 0 8px 0', borderBottom: `1px solid ${K.borde}` }}>
        <span>Lo que debemos · corte a fin de mes</span><span style={{ textAlign: 'right' }}>Pagar en {mesCorte}</span>{!isMobile && <span style={{ textAlign: 'right' }}>Meses siguientes</span>}<span />
      </div>
      {acordeon('prov', 'Proveedores', c.prov, null)}
      {acordeon('hot', 'Hoteles', c.hot, 'Sin hoteles por pagar. Aquí sale cada hotel con su fecha límite de pago.')}
      {acordeon('vue', 'Aerolíneas', c.vue, 'Sin vuelos por pagar. Los vuelos son prepago: salen el día que se reservan.', true)}
    </div>
  );

  // ── Gastos del mes + final ──
  const totalGastos = c.gastos.fijo + c.gastos.adicional + c.t.com;
  const lateral = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ ...panel, padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ ...rotulo, paddingBottom: 8 }}>GASTOS DEL MES</span>
        {[['Gastos fijos', c.gastos.fijo], ['Gastos adicionales', c.gastos.adicional], ['Comisiones pagadas', c.t.com]].map(([l, v]) => (
          <div key={l} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, padding: '8px 0', borderBottom: `1px solid ${K.sep}` }}>
            <span>{l}</span><span style={cifra({ color: v ? K.texto : K.tenue })}>{money(v)}</span>
          </div>
        ))}
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 16, fontWeight: 700, padding: '10px 0 0 0' }}><span>Total</span><span style={cifra()}>{money(totalGastos)}</span></div>
      </div>
      <div style={{ background: K.tarjeta, border: `1px solid ${K.tarjetaBorde}`, borderRadius: 16, padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 6, color: '#ffffff' }}>
        <span style={{ fontSize: 12, letterSpacing: '0.14em', color: K.tarjetaTexto, fontWeight: 700 }}>CUANDO TODO SE COBRE Y SE PAGUE</span>
        <span style={cifra({ fontSize: 30, fontWeight: 600 })}>{money(c.final)}</span>
        <span style={{ fontSize: 14, color: K.tarjetaTexto }}>En banco + por cobrar − por pagar{sinSaldo ? ' · sin saldo inicial' : ''}</span>
      </div>
    </div>
  );

  return (
    <div style={raiz} data-seccion="balance-real">
      {selector}
      {encabezado}
      {error && <div style={{ fontSize: 12, color: K.rojo }}>Aviso: una de las lecturas falló ({error}); las cifras pueden estar incompletas.</div>}
      <div style={{ display: 'flex', gap: isMobile ? 16 : 28, alignItems: 'stretch', flexDirection: isMobile ? 'column' : 'row' }}>
        {tarjeta}
        {deDonde}
      </div>
      {tres}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1.6fr) minmax(0, 1fr)', gap: 18, alignItems: 'start' }}>
        {deudas}
        {lateral}
      </div>
    </div>
  );
}
